import React from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import { SalesChannelType } from '@fd/customHooks/useQueryRMSSalesChannels';
import ArrowRightIcon from '@mui/icons-material/ArrowRightAlt';
import { Box, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { getTranslate } from 'react-localize-redux';
import { useHistory } from 'react-router-dom';
import { flagService } from 'services';
import { PortalBooleanFlagKeys } from 'services/ddFeatureFlags/booleanFlagKeys';

import { Button, Typography } from '@fd/ui/atoms';
import PageLayout from '@fd/ui/Layout';

import DeliverooIcon from '../../../assets/images/deliveroo-filled.svg';
import JustEatIcon from '../../../assets/images/just-eat-filled.svg';
import UberEatsIcon from '../../../assets/images/uber-eats-filled.svg';

const StyledCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: '8px',
  padding: theme.spacing(2),
  position: 'relative',
  backgroundColor: theme.palette.background.paper,
}));

const StyledIconWrapper = styled(Box)(() => ({
  backgroundColor: 'rgba(9, 96, 176, 0.05)',
  borderRadius: '50%',
  width: 48,
  height: 48,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  paddingLeft: theme.spacing(0),
  paddingRight: theme.spacing(0),
}));

const IntegrationsPage = () => {
  const history = useHistory();
  const showMarketplacesV2UberEats = useAppSelector((state) =>
    flagService.isFlagOn(state, 'marketplacesV2UberEats')
  );
  const showMarketplacesV2Deliveroo = useBooleanFlagValue(
    PortalBooleanFlagKeys.marketplaceIntegration_DeliverooV2,
    false
  );
  const translate = useAppSelector((state) => getTranslate(state?.locale));
  const currentApp = useAppSelector((state) => state.currentApp);

  const handleManageUberEatsClick = () => {
    if (showMarketplacesV2UberEats) {
      history.push(
        `/${currentApp?.AppId}/sales-channels/marketplaces/v2?type=${SalesChannelType.UBER_EATS}`
      );
    } else {
      history.push(
        `/${currentApp?.AppId}/sales-channels/marketplaces/v1?type=${SalesChannelType.UBER_EATS}`
      );
    }
  };

  const handleManageJustEatClick = () => {
    history.push(
      `/${currentApp?.AppId}/sales-channels/marketplaces/v1?type=${SalesChannelType.JUST_EATS}`
    );
  };

  const handleManageDeliverooClick = () => {
    if (showMarketplacesV2Deliveroo) {
      history.push(
        `/${currentApp?.AppId}/sales-channels/marketplaces/v2?type=${SalesChannelType.DELIVEROO}`
      );
    } else {
      history.push(
        `/${currentApp?.AppId}/sales-channels/marketplaces/v1?type=${SalesChannelType.DELIVEROO}`
      );
    }
  };

  const cards = [
    {
      icon: UberEatsIcon,
      title: translate('Uber_Eats'),
      description: translate('Uber_Eats_description'),
      buttonText: translate('Manage_Uber_Eats'),
      buttonClick: handleManageUberEatsClick,
      id: 'uber-eats',
    },
    {
      icon: JustEatIcon,
      title: translate('Just_Eat'),
      description: translate('Just_Eat_description'),
      buttonText: translate('Manage_Just_Eat'),
      buttonClick: handleManageJustEatClick,
      id: 'just-eat',
    },
    {
      icon: DeliverooIcon,
      title: translate('Deliveroo'),
      description: translate('Deliveroo_description'),
      buttonText: translate('Manage_Deliveroo'),
      buttonClick: handleManageDeliverooClick,
      id: 'deliveroo',
    },
  ];

  return (
    <PageLayout
      documentTitle="Marketplaces"
      title={
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {translate('Marketplaces')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {translate('Marketplaces_description')}
          </Typography>
        </Box>
      }
    >
      <Grid container spacing={2}>
        {cards.map((card) => {
          return (
            <Grid item xs={12} md={4} key={card.id}>
              <StyledCard>
                <Box className="flex items-start justify-between">
                  <StyledIconWrapper>
                    <img src={card.icon} alt={card.title.toString()} />
                  </StyledIconWrapper>
                </Box>
                <Typography variant="h5" fontWeight={600} mt={2} fontSize="1.125rem">
                  {card.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {card.description}
                </Typography>
                <StyledButton
                  fdKey={`manage-${card.id}-button`}
                  variant="text"
                  onClick={card.buttonClick}
                >
                  {card.buttonText}
                  <ArrowRightIcon />
                </StyledButton>
              </StyledCard>
            </Grid>
          );
        })}
      </Grid>
    </PageLayout>
  );
};

export default IntegrationsPage;
